import { createFileRoute } from "@tanstack/react-router"
import { addDays, startOfISOWeek } from "date-fns"
import { min } from "lodash"
import { useCallback, useEffect, useMemo, useRef } from "react"
import { useSessionStorage } from "usehooks-ts"
import { FORECAST_ANALYSIS_SNAPSHOT_KEY } from "~/hooks/useHistoricForecast"
import { CaretLeft, CaretRight, Sparkle } from "@phosphor-icons/react"

import VariantGraph from "~/components/product/VariantGraph"
import ForecastInputContextCard from "~/components/product/forecast-analysis/ForecastInputContextCard"
import ForecastModelBreakdownCard from "~/components/product/forecast-analysis/ForecastModelBreakdownCard"
import InternalForecastEvaluationCard from "~/components/product/forecast-analysis/InternalForecastEvaluationCard"
import NpiForecastModelComparisonCard from "~/components/product/forecast-analysis/NpiForecastModelComparisonCard"
import NpiForecastPipelineCard from "~/components/product/forecast-analysis/NpiForecastPipelineCard"
import NpiNearestNeighborsCard from "~/components/product/forecast-analysis/NpiNearestNeighborsCard"
import NpiSwitchStatusCard from "~/components/product/forecast-analysis/NpiSwitchStatusCard"
import SalesVsForecastCard from "~/components/product/forecast-analysis/SalesVsForecastCard"
import SimilarVariantsCard from "~/components/product/forecast-analysis/SimilarVariantsCard"
import { IconButton } from "~/components/reusable/Button"
import DatePicker from "~/components/reusable/DatePicker"
import { Label } from "~/components/reusable/Label"
import { Slider } from "~/components/reusable/Slider"
import { Tabs, type Tab } from "~/components/reusable/Tabs"
import { Tooltip } from "~/components/reusable/Tooltip"
import { useVariantContext } from "~/context/VariantProvider"
import { frozenForwardWindowEnd } from "~/hooks/useFrozenForwardWindow"
import { usePageTracking } from "~/hooks/usePageTracking"
import useShowInternalFeatures from "~/hooks/useShowInternalFeatures"
import { useDateRangeStore } from "~/providers/dateRangeStoreProvider"
import { useSkuStore } from "~/providers/sku-store-provider"
import { useUser } from "~/providers/userProvider"
import { api } from "~/utils/api"
import { convertToStartOfMonthUTC, formatDate, isSameDay, startOfTodayUTC } from "~/utils/datetime"
import { formatName } from "~/utils/namingUtils"
import { AnalysisPage } from "~/components/design-system/pages"
import VariantInspector from "~/components/product/VariantInspector"

export const Route = createFileRoute("/_auth/product/$productId/_layout/forecast-analysis")({
  component: RouteComponent,
})

function RouteComponent() {
  usePageTracking("Product - Forecast Analysis")

  const { permissions } = useUser()
  const [showInternalFeatures] = useShowInternalFeatures()
  const { variants } = useVariantContext()

  if (!permissions.isStaff || !showInternalFeatures) return null
  if (variants.length !== 1) {
    return <SelectSingleVariantPrompt count={variants.length} />
  }

  return <SingleVariantAnalysis />
}

function SingleVariantAnalysis() {
  const { variants } = useVariantContext()
  const variant = variants[0]!
  const isNpi = !!variant.isNpi

  useExpandToFullHorizon([variant.variantId], variant.soldSince ?? undefined)

  const tabs: Tab[] = [
    {
      label: `${formatName("sale", true, true)} based`,
      isDefault: !isNpi,
      content: <SalesBasedAnalysis />,
    },
    {
      label: "NPI",
      isDefault: isNpi,
      content: <NpiAnalysis />,
    },
    {
      label: "Similar variants",
      content: <SimilarVariantsAnalysis />,
    },
  ]

  return (
    <AnalysisPage inspector={<VariantInspector sections={["image", "details", "comments"]} />}>
      <div className="flex flex-col gap-4 min-h-full">
        <Tabs tabs={tabs} />
      </div>
    </AnalysisPage>
  )
}

function SalesBasedAnalysis() {
  const { variants } = useVariantContext()
  const variant = variants[0]!
  const variantIds = useMemo(() => [variant.variantId], [variant.variantId])
  const selectedWarehouseId = useSkuStore((state) => state.warehouseId)
  const selectedChannel = useSkuStore((state) => state.channel)
  const weeklyEndDate = useDateRangeStore((s) => s.weeklyEndDate)

  const { compareDate, setCompareDate, filterCompareDate, sortedAvailableDates } =
    useCompareDate(variantIds)

  return (
    <div className="flex flex-col gap-4 pt-2">
      <CompareToBar
        compareDate={compareDate}
        onChange={setCompareDate}
        filterDate={filterCompareDate}
        availableDates={sortedAvailableDates}
      />
      <div className="flex flex-row gap-4 items-stretch">
        <div className="flex-1 min-w-0">
          <VariantGraph
            warehouseId={selectedWarehouseId}
            channel={selectedChannel}
            graphHeight={320}
            netSalesMode="off"
            snapshotStorageKey={FORECAST_ANALYSIS_SNAPSHOT_KEY}
          />
        </div>
        <SalesVsForecastCard className="w-[280px] shrink-0" />
      </div>
      <ForecastInputContextCard
        variantId={variant.variantId}
        compareDate={compareDate}
        runDates={sortedAvailableDates}
      />
      <ForecastModelBreakdownCard
        variantId={variant.variantId}
        compareDate={compareDate}
        warehouseId={selectedWarehouseId}
        channel={selectedChannel}
        toDate={weeklyEndDate}
      />
      <InternalForecastEvaluationCard variantId={variant.variantId} />
    </div>
  )
}

function CompareToBar({
  compareDate,
  onChange,
  filterDate,
  availableDates,
}: {
  compareDate: Date | null
  onChange: (d: Date | null) => void
  filterDate: (d: Date) => boolean
  availableDates: Date[]
}) {
  // availableDates is sorted ascending (oldest first). Slider left = oldest, right = newest.
  const currentIndex = useMemo(() => {
    if (!compareDate) return availableDates.length - 1
    const idx = availableDates.findIndex((d) => isSameDay(d, compareDate))
    return idx >= 0 ? idx : availableDates.length - 1
  }, [compareDate, availableDates])

  const maxIndex = Math.max(0, availableDates.length - 1)
  const canStepBack = currentIndex > 0
  const canStepForward = currentIndex >= 0 && currentIndex < maxIndex

  const stepBy = (delta: number) => {
    const next = availableDates[currentIndex + delta]
    if (next) onChange(next)
  }

  const onSliderChange = (values: number[]) => {
    const idx = values[0]
    if (idx === undefined) return
    const next = availableDates[idx]
    if (next) onChange(next)
  }

  return (
    <div className="flex flex-col gap-3 rounded-md border border-base bg-base p-3">
      <div className="flex items-center gap-3 flex-wrap">
        <Label className="paragraph-sm text-fg-subtle whitespace-nowrap">
          Compare current forecast against
        </Label>
        <div className="flex items-center gap-1">
          <Tooltip title="Previous forecast run">
            <IconButton
              size="sm"
              variant="neutral"
              disabled={!canStepBack}
              onClick={() => stepBy(-1)}
            >
              <CaretLeft />
            </IconButton>
          </Tooltip>
          <div className="w-[180px]">
            <DatePicker
              value={compareDate ?? undefined}
              onChange={(d) => onChange(d ?? null)}
              filterDate={filterDate}
              clearable
              fullWidth
              placeholder="Pick a forecast run"
            />
          </div>
          <Tooltip title="Next forecast run">
            <IconButton
              size="sm"
              variant="neutral"
              disabled={!canStepForward}
              onClick={() => stepBy(1)}
            >
              <CaretRight />
            </IconButton>
          </Tooltip>
        </div>
        <span className="paragraph-sm text-fg-subtle">
          Drives the historic line on the forecast chart and the previous-run column in the model
          breakdown.
        </span>
      </div>
      {availableDates.length > 1 && (
        <div className="flex items-center gap-3">
          <span className="paragraph-sm text-fg-subtle whitespace-nowrap min-w-[90px]">
            {availableDates[0] ? formatDate(availableDates[0]) : "—"}
          </span>
          <Slider
            value={[currentIndex >= 0 ? currentIndex : maxIndex]}
            min={0}
            max={maxIndex}
            step={1}
            onValueChange={onSliderChange}
            className="flex-1"
          />
          <span className="paragraph-sm text-fg-subtle whitespace-nowrap min-w-[90px] text-right">
            {availableDates[maxIndex] ? formatDate(availableDates[maxIndex]) : "—"}
          </span>
        </div>
      )}
    </div>
  )
}

function NpiAnalysis() {
  const { variants } = useVariantContext()
  const variant = variants[0]!
  const variantIds = useMemo(() => [variant.variantId], [variant.variantId])
  const selectedWarehouseId = useSkuStore((state) => state.warehouseId)
  const selectedChannel = useSkuStore((state) => state.channel)
  const weeklyEndDate = useDateRangeStore((s) => s.weeklyEndDate)

  const { currentDate, compareDate, setCompareDate, filterCompareDate, sortedAvailableDates } =
    useNpiCompareDate(variantIds)

  return (
    <div className="flex flex-col gap-4 pt-2">
      <CompareToBar
        compareDate={compareDate}
        onChange={setCompareDate}
        filterDate={filterCompareDate}
        availableDates={sortedAvailableDates}
      />
      <NpiSwitchStatusCard
        variantId={variant.variantId}
        compareDate={compareDate}
        runDates={sortedAvailableDates}
        category={variant.category ?? null}
      />
      <NpiForecastModelComparisonCard
        variantId={variant.variantId}
        currentDate={currentDate}
        compareDate={compareDate}
      />
      <NpiNearestNeighborsCard variantId={variant.variantId} />
      <NpiForecastPipelineCard
        variantId={variant.variantId}
        warehouseId={selectedWarehouseId}
        channel={selectedChannel}
        toDate={weeklyEndDate}
      />
    </div>
  )
}

function SimilarVariantsAnalysis() {
  const { variants } = useVariantContext()
  const variant = variants[0]!
  return (
    <div className="flex flex-col gap-4 pt-2">
      <SimilarVariantsCard variantId={variant.variantId} />
    </div>
  )
}

function useNpiCompareDate(variantIds: string[]) {
  const { data: runDates } = api.bigQuery.npiVariantForecastRunDates.useQuery(
    { variantIds },
    { enabled: variantIds.length > 0 },
  )

  const sortedAvailableDates = useMemo(
    () => [...(runDates ?? [])].sort((a, b) => a.getTime() - b.getTime()),
    [runDates],
  )

  const currentDate = sortedAvailableDates.at(-1) ?? null

  // null = not yet initialized (use default), 0 = explicitly cleared, >0 = user-set date
  const [compareDateMs, setCompareDateMs] = useSessionStorage<number | null>(
    "npiHistoricForecastCompareDateMs",
    null,
  )

  const availableSet = useMemo(
    () => new Set(sortedAvailableDates.map((d) => d.getTime())),
    [sortedAvailableDates],
  )

  const filterCompareDate = useCallback((d: Date) => availableSet.has(d.getTime()), [availableSet])

  const setCompareDate = useCallback(
    (d: Date | null) => setCompareDateMs(d?.getTime() ?? 0),
    [setCompareDateMs],
  )

  const compareDate = useMemo(() => {
    if (compareDateMs === 0) return null
    if (compareDateMs !== null) return new Date(compareDateMs)
    return sortedAvailableDates.at(-2) ?? null
  }, [compareDateMs, sortedAvailableDates])

  return { currentDate, compareDate, setCompareDate, filterCompareDate, sortedAvailableDates }
}

function useExpandToFullHorizon(variantIds: string[], firstSale?: Date) {
  const weeklyStartDate = useDateRangeStore((s) => s.weeklyStartDate)
  const updateWeeklyDates = useDateRangeStore((s) => s.updateWeeklyDates)
  const updateMonthlyDates = useDateRangeStore((s) => s.updateMonthlyDates)
  const { data: maxHorizon } = api.forecasts.getMaxDataHorizonDate.useQuery(
    { variantIds },
    { enabled: variantIds.length > 0 },
  )
  const appliedRef = useRef(false)

  useEffect(() => {
    if (appliedRef.current) return
    if (!maxHorizon) return
    const newStart = firstSale ?? weeklyStartDate
    const newEnd = min([maxHorizon, frozenForwardWindowEnd()])!
    updateWeeklyDates(newStart, newEnd)
    updateMonthlyDates(convertToStartOfMonthUTC(newStart), convertToStartOfMonthUTC(newEnd))
    appliedRef.current = true
  }, [maxHorizon, firstSale, weeklyStartDate, updateWeeklyDates, updateMonthlyDates])
}

// Centralizes the "compare to" date for the whole tab. The picker is sourced from the
// Postgres ForecastVariantHistoric snapshot dates (same source VariantGraph's historic overlay
// uses), so the chosen date always renders on the forecast chart. The model breakdown card
// then resolves the chosen date to the closest available BQ run on/before, and surfaces a
// warning when that delta is large.
function useCompareDate(variantIds: string[]) {
  const [snapshotDateMs, setSnapshotDateMs] = useSessionStorage<number | null>(
    FORECAST_ANALYSIS_SNAPSHOT_KEY,
    null,
  )
  const [, setStoredWeekDiff] = useSessionStorage("historicForecastWeekDiff", 0)

  const { data: availableDates } = api.forecasts.getHistoricForecastDatesForVariants.useQuery(
    { variantIds },
    { enabled: variantIds.length > 0 },
  )

  const sortedAvailableDates = useMemo(
    () => [...(availableDates ?? [])].sort((a, b) => a.getTime() - b.getTime()),
    [availableDates],
  )

  const availableWeekSet = useMemo(() => {
    const set = new Set<number>()
    for (const d of sortedAvailableDates) set.add(startOfISOWeek(d).getTime())
    return set
  }, [sortedAvailableDates])

  const filterCompareDate = useCallback(
    (d: Date) => availableWeekSet.has(startOfISOWeek(d).getTime()),
    [availableWeekSet],
  )

  const resolveToSnapshotDate = useCallback(
    (picked: Date): Date | null => {
      if (sortedAvailableDates.length === 0) return null
      const pickedMs = picked.getTime()
      let best: Date | null = null
      for (const d of sortedAvailableDates) {
        const dMs = d.getTime()
        if (dMs <= pickedMs && (best === null || dMs > best.getTime())) best = d
      }
      return best
    },
    [sortedAvailableDates],
  )

  // Default once per mount: closest available snapshot on/before (today - 7d).
  const defaultedRef = useRef(false)
  useEffect(() => {
    if (defaultedRef.current) return
    if (sortedAvailableDates.length === 0) return
    if (snapshotDateMs !== null) {
      defaultedRef.current = true
      return
    }
    const target = addDays(startOfTodayUTC(), -7)
    const fallback = resolveToSnapshotDate(target)
    if (fallback) {
      setSnapshotDateMs(fallback.getTime())
      setStoredWeekDiff(0)
    }
    defaultedRef.current = true
  }, [
    sortedAvailableDates,
    snapshotDateMs,
    resolveToSnapshotDate,
    setSnapshotDateMs,
    setStoredWeekDiff,
  ])

  const setCompareDate = useCallback(
    (date: Date | null) => {
      const resolved = date ? resolveToSnapshotDate(date) : null
      setSnapshotDateMs(resolved?.getTime() ?? null)
      if (resolved) setStoredWeekDiff(0)
    },
    [resolveToSnapshotDate, setSnapshotDateMs, setStoredWeekDiff],
  )

  const compareDate = snapshotDateMs ? new Date(snapshotDateMs) : null

  return { compareDate, setCompareDate, filterCompareDate, sortedAvailableDates }
}

function SelectSingleVariantPrompt({ count }: { count: number }) {
  return (
    <div className="flex h-full items-center justify-center">
      <div className="flex flex-col items-center gap-3 max-w-md text-center p-6 rounded-md border border-base bg-base">
        <Sparkle size={28} className="fill-orange-600" />
        <h2 className="heading-sm text-fg-base">Select a single variant</h2>
        <p className="paragraph-base text-fg-subtle">
          {count === 0 ? "No variant is selected." : `${count} variants are currently selected.`}{" "}
          Forecast analysis runs on a single variant at a time. Use the variant selector at the top
          of the page to pick one.
        </p>
      </div>
    </div>
  )
}
